import { useQuery } from '@tanstack/react-query';
import { Badge, InlineError, Skeleton } from '../../components/ui';
import { useAuth } from '../auth/AuthProvider';
import { useProspectingAvailability } from './useProspectingAvailability';
import {
  currentMessageLabel,
  decodeMessageReport,
  messageRate,
  messageVersionLabel,
} from './messageReport';
import type { ScoreTier } from './linkedinScoreboard';

export function CurrentMessageReports({ tier }: { tier: ScoreTier }) {
  const { client, access } = useAuth();
  const actor = access?.status === 'approved' ? access.profile.id : null;
  const { reader_enabled } = useProspectingAvailability();
  const report = useQuery({
    queryKey: ['tracker', 'linkedin', 'current-message-report', actor],
    retry: false,
    enabled: reader_enabled,
    queryFn: async () => {
      const { data, error } = await client.rpc('read_linkedin_message_report');
      if (error) throw new Error(error.message);
      return decodeMessageReport(data);
    },
  });
  if (!reader_enabled) return null;
  if (report.isPending) return <Skeleton label="Loading current-message reports" lines={2} />;
  if (report.isError)
    return <InlineError>Current-message reports could not be loaded. Try refreshing.</InlineError>;
  const rows = report.data.messages.filter((row) => tier === 'all' || row.tier === tier);
  const slots = report.data.slots.filter((row) => tier === 'all' || row.tier === tier);
  const key = (row: { tier: string; copy_set_id: string | null; message_version: string | null }) =>
    `${row.tier}:${row.copy_set_id ?? 'none'}:${row.message_version ?? 'unknown'}`;
  return (
    <>
      <section aria-label={report.data.performance_label}>
        <h3>{report.data.performance_label}</h3>
        <p className="tracker-field-note">
          {report.data.scope}. {report.data.performance_policy}
        </p>
        {report.data.attribution_needs_review > 0 && (
          <InlineError>
            Attribution needs review: {report.data.attribution_needs_review} DM entries are
            excluded.
          </InlineError>
        )}
        <div className="tracker-table-scroll">
          <table className="tracker-perf-table">
            <thead>
              <tr>
                {[
                  'Message',
                  'Tier',
                  'People messaged',
                  'Replies',
                  'Reply %',
                  'Info replies',
                  'Calls booked',
                ].map((label) => (
                  <th key={label} scope="col">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={key(row)}>
                  <th scope="row">
                    {currentMessageLabel(row)} {row.draft && <Badge>Draft · One-off</Badge>}
                    <small> · {messageVersionLabel(row)}</small>
                  </th>
                  <td>{row.tier}</td>
                  <td>{row.people_messaged}</td>
                  <td>{row.replied}</td>
                  <td>{messageRate(row.replied, row.people_messaged)}</td>
                  <td>{row.info_replied}</td>
                  <td>{row.calls_booked}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && <p>No current prospect message assignments for this tier.</p>}
      </section>
      <section aria-label={report.data.slot_label}>
        <h3>{report.data.slot_label}</h3>
        <p className="tracker-field-note">{report.data.slot_policy}</p>
        <div className="tracker-table-scroll">
          <table className="tracker-perf-table">
            <thead>
              <tr>
                {['Message', 'Tier', 'Slot', 'DMs', 'People'].map((label) => (
                  <th key={label} scope="col">
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slots.map((row) => (
                <tr key={`${key(row)}:${row.message_slot ?? 'unknown'}`}>
                  <th scope="row">
                    {currentMessageLabel(row)} {row.draft && <Badge>Draft · One-off</Badge>}
                    <small> · {messageVersionLabel(row)}</small>
                  </th>
                  <td>{row.tier}</td>
                  <td>
                    {row.copy_set_id === null
                      ? 'Message not recorded'
                      : row.message_slot === null
                        ? 'Slot not recorded'
                        : `Slot ${row.message_slot}`}
                  </td>
                  <td>{row.dms_sent}</td>
                  <td>{row.people_messaged}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {slots.length === 0 && <p>No prospect DM slot usage for this tier.</p>}
      </section>
    </>
  );
}
